import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import merchant_services from "services/merchant_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import MERCHANT_UTIL from "utils/merchant";
import type { TerminalRecord } from "components/terminal-row/TerminalRow";
import type { PosByTreasury } from "utils/AxiosUtil";

/** Shared with useTerminalLookup so a chain-found terminal renders identically to a listed one. */
export function toTerminalRecord(t: PosByTreasury): TerminalRecord {
  return {
    serialId: t.serialId,
    serial: t.serial,
    status: MERCHANT_UTIL.getTerminalStatus(t.statusLabel),
    vendorDigit: t.vendorDigit,
    typeDigit: t.typeDigit,
  };
}

export function useTerminals(merchantId: string) {
  const queryClient = useQueryClient();
  const networkType = useSelector(networkTypeSelector);

  const query = useQuery<PosByTreasury[]>({
    queryKey: ["terminals", merchantId],
    queryFn: () => merchant_services.getTerminals(merchantId, networkType),
    enabled: !!merchantId,
  });

  const terminals: TerminalRecord[] = useMemo(
    () => query.data?.map(toTerminalRecord) ?? [],
    [query.data],
  );

  return {
    terminals,
    loading: query.isLoading,
    refresh: () =>
      queryClient.invalidateQueries({ queryKey: ["terminals", merchantId] }),
  };
}
